import { Component, Element, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { ClassNames } from '../../utils/interfaces';

@Component({
  tag: 'patternlib-headline',
  styleUrl: 'patternlib-headline.scss',
  shadow: true,
})
export class PatternlibHeadline {
  @Element() hostElement: HTMLPatternlibHeadlineElement;

  private headlineElement: HTMLElement;
  private headingClass: string;

  /**
   * Define if headline should be animated or not.
   */
  @Prop() animation: 'now' | 'never' | 'later' = 'now';

  /**
   * The animation delay.
   */
  @Prop() animationDelay: '0s';

  /**
   * Set the headline text.
   */
  @Prop() headline = '';

  /**
   * Define the heading tag.
   */
  @Prop({ mutable: true }) headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'display-1' | 'display-2' = 'display-1';

  /**
   * Define the hyphen style for the heading.
   */
  @Prop() hyphens: 'auto' | 'manual' | 'none' = 'manual';

  /**
   * Define the width of the container.
   */
  @Prop() width = 'auto';

  /**
   * Define the separator style.
   */
  @Prop() separator: 'none' | 'top-and-bottom' | 'bottom' = 'top-and-bottom';

  /**
   * Define the color of the headline text and separator
   */
  @Prop() color: 'black' | 'white' = 'black';

  /**
   * Internal state to save the headline width.
   */
  @State() headlineWidth: number;

  /**
   * Internal state to save the animation type.
   */
  @State() animationClass: 'now' | 'never' | 'later' = 'now';

  @Listen('resize', { target: 'window' })
  updateOnResize() {
    return this.updateHeadline();
  }

  @Watch('animation')
  updateAnimationClass() {
    this.animationClass = this.animation;
  }

  /**
   * Public function to animate the headline
   */
  @Method()
  async animateHeadline(animation: 'now' | 'never' | 'later') {
    this.animationClass = animation;
  }

  /**
   * Public function to update the headline
   */
  @Method()
  async updateHeadline() {
    if (this.headlineElement != null) {
      const elementWidth = this.headlineElement.offsetWidth;
      this.hostElement.style.setProperty('--headline-width', `${elementWidth}px`);
    }
  }

  componentWillLoad() {
    this.animationClass = this.animation;
    this.headingClass = this.headingTag;
    if (this.headingTag == 'display-1') {
      this.headingTag = 'h1';
    }
    if (this.headingTag == 'display-2') {
      this.headingTag = 'h2';
    }
  }

  componentDidRender() {
    return this.updateHeadline();
  }

  render() {
    const HeadingTag = `${this.headingTag}`;

    const separatorTop = () => {
      if (this.separator == 'top-and-bottom') {
        return <div class={separatorTopClass} style={seperatorStyles}></div>;
      } else {
        return null;
      }
    };
    const separatorBottom = () => {
      if (this.separator == 'top-and-bottom' || this.separator == 'bottom') {
        return <div class={separatorBottomClass} style={seperatorStyles}></div>;
      } else {
        return null;
      }
    };

    const seperatorStyles = { 'animation-delay': this.animationDelay };

    const headlineStyles = { 'width': this.width, 'animation-delay': this.animationDelay };
    const headingStyles = {
      hyphens: this.hyphens,
    };

    const headlineClass: ClassNames = {
      'headline-container': true,
      [this.separator]: true,
      [this.animationClass]: true,
      [this.headingClass]: true,
      'white-font': this.color === 'white',
    };
    const separatorTopClass: ClassNames = {
      'card-separator': true,
      'card-separator-top': true,
      'white': this.color === 'white',
    };
    const separatorBottomClass: ClassNames = {
      'card-separator': true,
      'card-separator-bottom': this.separator == 'top-and-bottom',
      'card-separator-bottom-only': this.separator == 'bottom',
      'white': this.color === 'white',
    };

    return (
      <Host>
        <div class={headlineClass} style={headlineStyles}>
          {separatorTop()}
          <HeadingTag class={this.headingClass} style={headingStyles} ref={el => (this.headlineElement = el as HTMLElement)}>
            {this.headline}
            <slot></slot>
          </HeadingTag>
          {separatorBottom()}
        </div>
      </Host>
    );
  }
}
